import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ScoringRule } from '@/types/scoring';
import { buildScoringWeights, calculateTrashPoints } from '@/lib/trashScoring';

export interface RankedPlayer {
  rank: number;
  player_id: string;
  full_name: string;
  position_abbreviation: string;
  position_eligibility: Record<string, number> | null;
  mlb_team_abbreviation: string | null;
  headshot_url: string | null;
  is_pitcher: boolean;
  games_played: number;
  trash_points: number;
  anti_points: number;
  net_points: number;
  // Key stat breakdowns
  strikeouts: number;
  errors: number;
  gidp: number;
  earned_runs: number;
  blown_saves: number;
  home_runs: number;
  hits: number;
  rbi: number;
  stolen_bases: number;
  pitcher_strikeouts: number;
  pitcher_wins: number;
  saves: number;
}

// All player_season_stats columns needed for scoring
const SEASON_STATS_SELECT = [
  'player_id', 'games_played', 'is_pitcher',
  'strikeouts', 'errors', 'gidp', 'caught_stealing',
  'earned_runs', 'walks_allowed', 'wild_pitches', 'blown_saves',
  'hit_batsmen', 'home_runs_allowed', 'balks', 'passed_balls',
  'at_bats', 'hits', 'doubles', 'triples', 'home_runs',
  'rbi', 'runs_scored', 'stolen_bases', 'walks_drawn', 'hit_by_pitch',
  'pitcher_strikeouts', 'pitcher_wins', 'pitcher_losses', 'saves', 'holds',
  'outfield_assists', 'innings_pitched', 'hits_allowed', 'throwing_errors',
  'left_on_base', 'complete_games', 'shutouts',
].join(', ');

/**
 * Compute trash/anti points from season stats using league scoring weights.
 * Returns { trash, anti, net } where trash = sum of positive-weight contributions,
 * anti = sum of negative-weight contributions, net = total.
 */
export function computeSeasonPoints(
  stats: Record<string, any>,
  weights: Record<string, number>,
): { trash: number; anti: number; net: number } {
  let trash = 0;
  let anti = 0;
  for (const [col, weight] of Object.entries(weights)) {
    let contribution = 0;
    if (col === '__derived_outs') {
      const outs = (Number(stats.at_bats) || 0) - (Number(stats.hits) || 0);
      contribution = outs * weight;
    } else if (col === '__derived_total_bases') {
      const tb = (Number(stats.hits) || 0) + (Number(stats.doubles) || 0) + 2 * (Number(stats.triples) || 0) + 3 * (Number(stats.home_runs) || 0);
      contribution = tb * weight;
    } else if (col === '__derived_pitcher_outs') {
      const pOuts = Math.round((Number(stats.innings_pitched) || 0) * 3);
      contribution = pOuts * weight;
    } else {
      contribution = (Number(stats[col]) || 0) * weight;
    }
    if (weight >= 0) {
      trash += contribution;
    } else {
      anti += contribution;
    }
  }
  return { trash, anti, net: trash + anti };
}

export function useTrashRankings(scoringRules?: ScoringRule[], season: number = 2026, _includeUnrostered: boolean = false) {
  const [rankedData, setRankedData] = useState<RankedPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);

  // Build weights from scoring rules (stable reference via JSON key)
  const rulesKey = scoringRules ? JSON.stringify(scoringRules.map(r => `${r.id}:${r.pointValue}:${r.isEnabled}`)) : '';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      // Build scoring weights from league rules
      const categoryConfig = scoringRules ? { scoring_rules: scoringRules } : null;
      const weights = buildScoringWeights(categoryConfig);

      // Fetch all active MLB players
      const pageSize = 1000;
      let allPlayers: any[] = [];
      let pFrom = 0;
      while (true) {
        const { data: pBatch, error: pErr } = await supabase
          .from('players')
          .select('id, full_name, position_abbreviation, position_eligibility, mlb_team_abbreviation, headshot_url, position_type')
          .eq('sport', 'mlb')
          .eq('status', 'Active')
          .range(pFrom, pFrom + pageSize - 1);
        if (pErr) break;
        allPlayers = allPlayers.concat(pBatch || []);
        if (!pBatch || pBatch.length < pageSize) break;
        pFrom += pageSize;
      }

      // Fetch full season stats for all players
      let allSeasonStats: any[] = [];
      let ssFrom = 0;
      while (true) {
        const { data: ssBatch, error: ssErr } = await supabase
          .from('player_season_stats')
          .select(SEASON_STATS_SELECT)
          .eq('season', season)
          .range(ssFrom, ssFrom + pageSize - 1);
        if (ssErr) break;
        allSeasonStats = allSeasonStats.concat(ssBatch || []);
        if (!ssBatch || ssBatch.length < pageSize) break;
        ssFrom += pageSize;
      }
      const seasonStatsMap = new Map<string, any>();
      for (const ss of allSeasonStats) {
        seasonStatsMap.set(ss.player_id, ss);
      }

      setHasData(allSeasonStats.length > 0);

      // Compute points from season stats + scoring weights
      const ranked: RankedPlayer[] = allPlayers.map(p => {
        const ss = seasonStatsMap.get(p.id);
        const { trash, anti, net } = ss ? computeSeasonPoints(ss, weights) : { trash: 0, anti: 0, net: 0 };
        return {
          rank: 0,
          player_id: p.id,
          full_name: p.full_name,
          position_abbreviation: p.position_abbreviation,
          position_eligibility: p.position_eligibility || null,
          mlb_team_abbreviation: p.mlb_team_abbreviation,
          headshot_url: p.headshot_url,
          is_pitcher: ss?.is_pitcher ?? p.position_type === 'Pitcher',
          games_played: ss?.games_played || 0,
          trash_points: trash,
          anti_points: anti,
          net_points: net,
          strikeouts: ss?.strikeouts || 0,
          errors: ss?.errors || 0,
          gidp: ss?.gidp || 0,
          earned_runs: ss?.earned_runs || 0,
          blown_saves: ss?.blown_saves || 0,
          home_runs: ss?.home_runs || 0,
          hits: ss?.hits || 0,
          rbi: ss?.rbi || 0,
          stolen_bases: ss?.stolen_bases || 0,
          pitcher_strikeouts: ss?.pitcher_strikeouts || 0,
          pitcher_wins: ss?.pitcher_wins || 0,
          saves: ss?.saves || 0,
        };
      });

      // Sort by net trash points descending
      ranked.sort((a, b) => b.net_points - a.net_points);
      ranked.forEach((p, i) => { p.rank = i + 1; });

      setRankedData(ranked);
      setLoading(false);
    };

    fetchData();
  }, [season, rulesKey]);

  return { rankings: rankedData, loading, hasData };
}
